import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { 
  CheckCircle2, 
  Copy, 
  GitPullRequest, 
  Terminal, 
  Code2,
  Cloud,
  Cpu,
  Container,
  GitBranch
} from 'lucide-react';

const integrations = [
  {
    id: 'github-app',
    label: 'GitHub App (Recommended)',
    title: 'One-Click GitHub Integration',
    description: 'The fastest way to get started. Install our GitHub App and instantly track all your repository changes.',
    icon: GitBranch,
    language: 'json',
    code: `// 1. Install DeltaOps GitHub App from the GitHub Marketplace
// 2. Select repositories and target branches to monitor
// 3. That's it! Changes are automatically tracked

// Example of an automatically generated changelog event:
{
  "event_type": "github_commit",
  "changelog_event": {
    "title": "Updated authentication middleware",
    "description": "Commit message: Add rate limiting to auth endpoints\\n\\nChanges include:\\n- Added rate limiting middleware\\n- Updated tests\\n- Added configuration options",
    "source_type": "github",
    "source_link": "https://github.com/your-org/your-repo/commit/abc123",
    "tags": ["backend", "security", "middleware"],
    "metadata": {
      "commit_hash": "abc123def456",
      "repository": "your-org/your-repo",
      "branch": "main",
      "author": "jane.smith",
      "pull_request": {
        "number": 123,
        "title": "Add auth rate limiting",
        "status": "merged",
        "reviewers": ["john.doe", "mary.jones"]
      },
      "files_changed": [
        "src/middleware/auth.js",
        "tests/auth.test.js",
        "config/auth.json"
      ],
      "stats": {
        "additions": 156,
        "deletions": 23,
        "files_changed": 3
      }
    }
  }
}`,
  },
  {
    id: 'lambda',
    label: 'AWS Lambda',
    title: 'AWS Lambda Integration',
    description: 'Track changes from AWS Lambda functions using the fetch API.',
    icon: Cloud,
    language: 'javascript',
    code: `// Lambda function that logs changes to DeltaOps
exports.handler = async (event, context) => {
  const response = await fetch('https://api.deltaops.app/api/deltaops/changelog/', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer \${process.env.DELTAOPS_API_TOKEN}',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      title: 'Lambda Function Executed',
      description: \`Function \${context.functionName} executed in \${context.region}\`,
      tags: ['lambda', 'aws'],
      source_type: 'lambda',
      environment: context.functionVersion === '$LATEST' ? 'development' : 'production',
      severity: 'low',
      is_critical: false,
      metadata: {
        aws_request_id: context.awsRequestId,
        function_name: context.functionName,
        function_version: context.functionVersion,
        region: context.region,
        memory_limit: context.memoryLimitInMB,
        event: event
      }
    })
  });

  return {
    statusCode: 200,
    body: JSON.stringify({ message: 'Change logged successfully' })
  };
};`
  },
  {
    id: 'kubernetes',
    label: 'Kubernetes',
    title: 'Kubernetes Integration',
    description: 'Track pod lifecycle events using a simple container.',
    icon: Container,
    language: 'yaml',
    code: `apiVersion: batch/v1
kind: Job
metadata:
  name: track-deployment
spec:
  template:
    spec:
      containers:
      - name: track-changes
        image: curlimages/curl
        command: ["curl"]
        args:
        - "-X"
        - "POST"
        - "https://api.deltaops.app/api/deltaops/changelog/"
        - "-H"
        - "Authorization: Bearer $(DELTAOPS_TOKEN)"
        - "-H"
        - "Content-Type: application/json"
        - "-d"
        - |
          {
            "title": "Updated Production Deployment",
            "description": "Rolling update of web-frontend to v2.1.0",
            "tags": ["kubernetes", "deployment"],
            "source_type": "kubernetes",
            "environment": "production",
            "severity": "medium",
            "is_critical": false,
            "metadata": {
              "namespace": "production",
              "deployment": "web-frontend",
              "version": "v2.1.0",
              "replicas": 3
            }
          }
        env:
        - name: DELTAOPS_TOKEN
          valueFrom:
            secretKeyRef:
              name: deltaops-secret
              key: api-token
      restartPolicy: Never`
  },
  {
    id: 'terraform',
    label: 'Terraform',
    title: 'Terraform Integration',
    description: 'Track infrastructure changes using local-exec provisioner.',
    icon: Cloud,
    language: 'hcl',
    code: `resource "null_resource" "track_changes" {
  # Trigger on any resource change you want to track
  triggers = {
    instance_id = aws_instance.example.id
  }

  provisioner "local-exec" {
    command = <<-EOF
      curl -X POST "https://api.deltaops.app/api/deltaops/changelog/" \\
        -H "Authorization: Bearer \${var.deltaops_token}" \\
        -H "Content-Type: application/json" \\
        -d '{
          "title": "Infrastructure Update",
          "description": "EC2 instance modified",
          "tags": ["terraform", "aws", "ec2"],
          "source_type": "terraform",
          "environment": "\${terraform.workspace}",
          "severity": "medium",
          "is_critical": false,
          "metadata": {
            "resource_type": "aws_instance",
            "instance_id": "\${aws_instance.example.id}",
            "workspace": "\${terraform.workspace}"
          }
        }'
    EOF
  }
}`
  },
  {
    id: 'swift',
    label: 'Swift',
    title: 'Swift Integration',
    description: 'Track changes from iOS or macOS applications.',
    icon: Terminal,
    language: 'swift',
    code: `import Foundation

struct ChangeEvent: Codable {
    let title: String
    let description: String
    let tags: [String]
    let source_type: String
    let environment: String
    let severity: String
    let is_critical: Bool
    let metadata: [String: Any]
    
    enum CodingKeys: String, CodingKey {
        case title, description, tags, source_type, environment, severity, is_critical, metadata
    }
}

func logChange(title: String, description: String, metadata: [String: Any]) async throws {
    guard let url = URL(string: "https://api.deltaops.app/api/deltaops/changelog/") else {
        throw URLError(.badURL)
    }
    
    var request = URLRequest(url: url)
    request.httpMethod = "POST"
    request.setValue("Bearer YOUR_API_TOKEN", forHTTPHeaderField: "Authorization")
    request.setValue("application/json", forHTTPHeaderField: "Content-Type")
    
    let event = ChangeEvent(
        title: title,
        description: description,
        tags: ["ios", "mobile"],
        source_type: "ios_app",
        environment: "production",
        severity: "low",
        is_critical: false,
        metadata: metadata
    )
    
    let jsonData = try JSONEncoder().encode(event)
    request.httpBody = jsonData
    
    let (data, response) = try await URLSession.shared.data(for: request)
    
    guard let httpResponse = response as? HTTPURLResponse,
          (200...299).contains(httpResponse.statusCode) else {
        throw URLError(.badServerResponse)
    }
}`
  },
  {
    id: 'cpp',
    label: 'C++',
    title: 'C++ Integration',
    description: 'Track changes from C++ applications using libcurl.',
    icon: Cpu,
    language: 'cpp',
    code: `#include <curl/curl.h>
#include <string>
#include <json/json.h>

class ChangeLogger {
public:
    static bool logChange(const std::string& title, 
                         const std::string& description,
                         const Json::Value& metadata) {
        CURL* curl = curl_easy_init();
        if (!curl) return false;

        Json::Value event;
        event["title"] = title;
        event["description"] = description;
        event["tags"] = Json::Value(Json::arrayValue);
        event["tags"].append("cpp");
        event["tags"].append("backend");
        event["source_type"] = "cpp_application";
        event["environment"] = "production";
        event["severity"] = "low";
        event["is_critical"] = false;
        event["metadata"] = metadata;

        Json::StreamWriterBuilder writer;
        std::string jsonBody = Json::writeString(writer, event);

        struct curl_slist* headers = nullptr;
        headers = curl_slist_append(headers, "Content-Type: application/json");
        headers = curl_slist_append(headers, "Authorization: Bearer YOUR_API_TOKEN");

        curl_easy_setopt(curl, CURLOPT_URL, "https://api.deltaops.app/api/deltaops/changelog/");
        curl_easy_setopt(curl, CURLOPT_POST, 1L);
        curl_easy_setopt(curl, CURLOPT_POSTFIELDS, jsonBody.c_str());
        curl_easy_setopt(curl, CURLOPT_HTTPHEADER, headers);

        CURLcode res = curl_easy_perform(curl);
        bool success = (res == CURLE_OK);

        curl_slist_free_all(headers);
        curl_easy_cleanup(curl);

        return success;
    }
};

// Example usage
int main() {
    Json::Value metadata;
    metadata["system"] = "production";
    metadata["component"] = "data-processor";
    
    ChangeLogger::logChange(
        "Data Processing Complete",
        "Processed 1000 records successfully",
        metadata
    );
    
    return 0;
}`
  },
  {
    id: 'go',
    label: 'Go',
    title: 'Go Integration',
    description: 'Track changes from Go applications.',
    icon: Terminal,
    language: 'go',
    code: `package main

import (
    "bytes"
    "encoding/json"
    "net/http"
)

type ChangeEvent struct {
    Title       string                 \`json:"title"\`
    Description string                 \`json:"description"\`
    Tags        []string              \`json:"tags"\`
    SourceType  string                \`json:"source_type"\`
    Environment string                \`json:"environment"\`
    Severity    string                \`json:"severity"\`
    IsCritical  bool                  \`json:"is_critical"\`
    Metadata    map[string]interface{} \`json:"metadata"\`
}

func logChange(title, description string, metadata map[string]interface{}) error {
    event := ChangeEvent{
        Title:       title,
        Description: description,
        Tags:        []string{"golang", "backend"},
        SourceType:  "go_application",
        Environment: "production",
        Severity:    "low",
        IsCritical:  false,
        Metadata:    metadata,
    }

    jsonData, err := json.Marshal(event)
    if err != nil {
        return err
    }

    req, err := http.NewRequest("POST", "https://api.deltaops.app/api/deltaops/changelog/", 
        bytes.NewBuffer(jsonData))
    if err != nil {
        return err
    }

    req.Header.Set("Authorization", "Bearer YOUR_API_TOKEN")
    req.Header.Set("Content-Type", "application/json")

    client := &http.Client{}
    resp, err := client.Do(req)
    if err != nil {
        return err
    }
    defer resp.Body.Close()

    return nil
}

// Example usage
func main() {
    metadata := map[string]interface{}{
        "service": "api-gateway",
        "environment": "production",
    }

    err := logChange(
        "API Gateway Updated",
        "Updated rate limiting configuration",
        metadata,
    )
    if err != nil {
        panic(err)
    }
}`
  },
  {
    id: 'javascript',
    label: 'JavaScript',
    title: 'JavaScript Integration',
    description: 'Track changes from Node.js or browser applications using fetch.',
    icon: Code2,
    language: 'javascript',
    code: `// Example of sending a change event from your frontend/Node.js app
const response = await fetch('https://api.deltaops.app/api/deltaops/changelog/', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    title: 'Updated API Gateway',
    description: 'Switched primary endpoint to EU region',
    tags: ['api', 'infrastructure'],
    source_type: 'manual',
    environment: 'production',
    severity: 'medium',
    is_critical: false,
    metadata: {
      region: 'eu-west-1',
      affected_services: ['auth', 'payments'],
      initiator: 'jane@company.com'
    }
  })
});

if (!response.ok) {
  console.error('Failed to log change:', await response.text());
  return;
}

// Success! The event has been logged
const result = await response.json();
console.log('Change logged with ID:', result.id);`
  },
  {
    id: 'python',
    label: 'Python',
    title: 'Python Integration',
    description: 'Track changes from Python scripts and applications using requests.',
    icon: Terminal,
    language: 'python',
    code: `import requests
from datetime import datetime

def log_change(title, description, metadata=None):
    """Log a change to DeltaOps API"""
    headers = {
        'Authorization': f'Bearer {DELTAOPS_API_TOKEN}',
        'Content-Type': 'application/json'
    }
    
    data = {
        'title': title,
        'description': description,
        'tags': ['python', 'automated'],
        'source_type': 'script',
        'environment': 'production',
        'severity': 'low',
        'is_critical': False,
        'metadata': metadata or {}
    }
    
    response = requests.post(
        'https://api.deltaops.app/api/deltaops/changelog/',
        headers=headers,
        json=data
    )
    response.raise_for_status()
    return response.json()

# Example usage
try:
    # Your maintenance task here
    affected_users = update_user_preferences()
    
    log_change(
        title='Updated User Preferences',
        description='Applied new default settings',
        metadata={
            'affected_users': affected_users,
            'environment': 'production'
        }
    )
except Exception as e:
    log_change(
        title='Maintenance Task Failed',
        description=str(e),
        metadata={'error': True}
    )
    raise`
  },
  {
    id: 'git-hooks',
    label: 'Git Hooks',
    title: 'Track Code Changes',
    description: 'Log commits and PRs automatically using simple Git hooks and cURL.',
    icon: GitPullRequest,
    language: 'bash',
    code: `#!/bin/bash
# .git/hooks/post-commit

COMMIT_SHA=$(git rev-parse HEAD)
BRANCH_NAME=$(git rev-parse --abbrev-ref HEAD)
MESSAGE=$(git log -1 --pretty=%B)

curl -X POST "https://api.deltaops.app/api/deltaops/changelog/" \\
  -H "Authorization: Bearer $DELTAOPS_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d "{
    \\"title\\": \\"Git Commit: \${COMMIT_SHA:0:8}\\",
    \\"description\\": \\"$MESSAGE\\",
    \\"tags\\": [\\"git\\", \\"code-change\\"],
    \\"source_type\\": \\"git\\",
    \\"environment\\": \\"$BRANCH_NAME\\",
    \\"severity\\": \\"low\\",
    \\"is_critical\\": false,
    \\"metadata\\": {
      \\"commit_sha\\": \\"$COMMIT_SHA\\",
      \\"branch\\": \\"$BRANCH_NAME\\",
      \\"author\\": \\"$(git config user.email)\\"
    }
  }"`
  }
];

const SetupGuideSection = () => {
  const [activeTab, setActiveTab] = useState(integrations[0].id);
  const [copiedStates, setCopiedStates] = useState({});

  const activeIntegration = integrations.find(tab => tab.id === activeTab);

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedStates({ ...copiedStates, [activeTab]: true });
    setTimeout(() => {
      setCopiedStates({ ...copiedStates, [activeTab]: false });
    }, 2000);
  };

  return (
        <Card className="overflow-hidden border-0 shadow-xl bg-white">
          <div className="flex flex-col lg:flex-row">
            {/* Left panel - Navigation */}
            <div className="lg:w-72 bg-gray-50 p-6 border-r border-gray-200">
              <div className="space-y-1">
                {integrations.map((integration) => {
                  const Icon = integration.icon;
                  return (
                    <button
                      key={integration.id}
                      onClick={() => setActiveTab(integration.id)}
                      className={`w-full flex items-center px-4 py-3 rounded-lg text-left transition-colors ${
                        activeTab === integration.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      <span className="font-medium">{integration.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right panel - Content */}
            <div className="flex-1 p-6">
              <div className="max-w-3xl">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {activeIntegration.title}
                  </h3>
                  <p className="text-gray-600">{activeIntegration.description}</p>
                </div>

                {/* Code section */}
                <div className="relative">
                  <div className="absolute right-4 top-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(activeIntegration.code)}
                      className={`transition-colors ${
                        copiedStates[activeTab]
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-white'
                      }`}
                    >
                      {copiedStates[activeTab] ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Code
                        </>
                      )}
                    </Button>
                  </div>
                  <pre className="bg-gray-900 text-gray-100 p-6 rounded-xl overflow-x-auto">
                    <code className={`language-${activeIntegration.language}`}>
                      {activeIntegration.code}
                    </code>
                  </pre>
                </div>

                {/* Quick tip */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="flex">
                    <Code2 className="w-5 h-5 text-blue-700 mt-1 mr-3" />
                    <div>
                      <h6 className="font-medium text-blue-900 mb-1">API Tips</h6>
                      <p className="text-sm text-blue-700">
                        Get your API key from the <a href="https://console.deltaops.app/settings" className="underline hover:text-blue-800">console</a>. 
                        Need help? Check our{' '}
                        <a href="/docs" className="underline hover:text-blue-800">API documentation</a>
                         {/* or view{' '}
                        <a href="/examples" className="underline hover:text-blue-800">more examples</a>. */}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
  );
};

export default SetupGuideSection;